<template>
  <div class="br-contact-us-drawer">
    <GeetestCaptcha
      ref="contactUsCaptcha"
      :current-url-name="geetestUrlName"
      @captchaVerifyCallbackGeetest="finishContactUsSlideGeetest"
    />
    <ContactUsDrawer
      :show="showModal"
      :form-data="form"
      :reason-options="reasonOptions"
      :country-options="countryOptions"
      :loading="loading"
      :submitting="submitting"
      :hide-button="true"
      :simple="true"
      @update:formData="onFormUpdate"
      @submit="onSubmit"
      @close="close"
    />
    <ContactUsSuccessModal :show="successModalVisible" @close="successModalVisible = false" />
  </div>
</template>

<script>
import GeetestCaptcha from '../../../components/GeetestCaptcha.vue';
import ContactUsDrawer from '../../../components/new-login/ContactUs/ContactUsDrawer.vue';
import ContactUsSuccessModal from '../../../components/new-login/ContactUs/ContactUsSuccessModal.vue';
import { AlertDialog } from '../../../_helpers/AlertDialog';
import { userService, CompanyInfoService } from '../../../_services';
import { getContactUsConfig, DEFAULT_LANG_ID } from '../../../components/new-login/Common/new-login-config.js';
import { detectLocationByIP, autoDetectCountryCode } from '../../../components/new-login/Common/ip-geolocation.js';
import { findOptionText } from '../../../components/new-login/Common/utils.js';

const REASON_CONFIG = [
  { value: 1, text: '我需要协助,请联系我。', langid: 936 },
  { value: 1, text: '我需要協助,請聯繫我。', langid: 950 },
  { value: 1, text: 'I need assistance. Please contact me.', langid: 1252 },
  { value: 2, text: '我想更改主帐号的密码，请联系我。', langid: 936 },
  { value: 2, text: '我想更改主帳號的密碼，請聯繫我。', langid: 950 },
  { value: 2, text: 'I want to update the password of the master account. Please contact me.', langid: 1252 },
];

function currentLangId() {
  return parseInt(localStorage.getItem('currentLangId')) || DEFAULT_LANG_ID;
}

function defaultReasonText(langId) {
  if (langId === 936) return '我需要协助,请联系我。';
  if (langId === 950) return '我需要協助,請聯繫我。';
  return 'I need assistance. Please contact me.';
}

function createForm() {
  const langId = currentLangId();
  const cfg = getContactUsConfig('supplier');
  return {
    fid: cfg.fid,
    hidCodepage: String(langId),
    showid: cfg.showid,
    showcode: cfg.showcode,
    langid: langId,
    Reason: 1,
    ReasonText: defaultReasonText(langId),
    message: '',
    name: '',
    Company: '',
    Country: '',
    CountryText: '',
    Mobile: '',
    Email: '',
    hdPrivacy: false,
    hdShare: true,
    captchaVerifyParam: '',
    submitPageURL: 'BusinessRegistrationContactUs',
    IsNewValidCode: 'true',
    isNotNeedValidate: 'false',
  };
}

export default {
  name: 'BrContactUsDrawer',
  components: { GeetestCaptcha, ContactUsDrawer, ContactUsSuccessModal },
  mixins: [AlertDialog],
  data() {
    return {
      showModal: false,
      successModalVisible: false,
      loading: false,
      submitting: false,
      reasonOptions: [],
      countryOptions: [],
      loginInfo: null,
      companyInfo: null,
      currentUserAccount: null,
      ipLocationInfo: null,
      contextLoaded: false,
      form: createForm(),
    };
  },
  computed: {
    geetestUrlName() {
      if (process.env.VUE_APP_EMP_ENV_NAME === 'UAT') return 'eMarketplaceUat_Back';
      if (process.env.VUE_APP_EMP_ENV_NAME === 'AI') return 'eMarketplaceAi_Back';
      return 'eMarketplace_Back';
    },
  },
  methods: {
    async open() {
      if (!this.contextLoaded) {
        await this.loadContext();
      }
      this.showModal = true;
      if (this.reasonOptions.length === 0) {
        this.loadReasons();
      }
      if (this.countryOptions.length === 0) {
        await this.loadCountries();
      }
      this.$nextTick(() => this.fillUserInfo());
    },
    close() {
      this.showModal = false;
    },
    async loadContext() {
      this.loadReasons();
      await Promise.all([
        this.fetchLoginInfo(),
        this.fetchCurrentUserAccount(),
        this.fetchCompanyInfo(),
        this.detectIPLocation(),
      ]);
      this.contextLoaded = true;
    },
    loadReasons() {
      const langId = this.form.langid || currentLangId();
      this.reasonOptions = REASON_CONFIG.filter((item) => item.langid === langId);
    },
    async loadCountries() {
      this.loading = true;
      try {
        const { loadCountryOptions } = await import('../../../components/new-login/Common/country-loader.js');
        this.countryOptions = await loadCountryOptions(this.form.langid || currentLangId());
      } catch (error) {
        this.countryOptions = [];
      } finally {
        this.loading = false;
      }
    },
    onFormUpdate(newFormData) {
      this.form = { ...this.form, ...newFormData };
      if (newFormData.Reason !== undefined) {
        this.form.ReasonText = findOptionText(this.reasonOptions, newFormData.Reason);
      }
      if (newFormData.Country !== undefined) {
        this.form.CountryText = findOptionText(this.countryOptions, newFormData.Country);
      }
    },
    async onSubmit() {
      const formEl = document.getElementById('cu-form-contact-us');
      if (!formEl) {
        this.$Alert(0, this.$t('lbl_EMPBE_Save_Failed_HasError', { msg: 'Form not found' }));
        return;
      }
      if (!formEl.checkValidity()) {
        formEl.reportValidity();
        return;
      }
      this.fillUserInfo();
      this.submitting = true;
      this.loading = true;
      await this.$nextTick();
      await this.submitForm();
    },
    async finishContactUsSlideGeetest(captchaVerifyParam, resolve) {
      this.form.captchaVerifyParam = JSON.stringify(captchaVerifyParam || {});
      if (captchaVerifyParam) {
        const fields = {
          'cu-lot-number': captchaVerifyParam.lot_number || '',
          'cu-captcha-output': captchaVerifyParam.captcha_output || '',
          'cu-pass-token': captchaVerifyParam.pass_token || '',
          'cu-gen-time': captchaVerifyParam.gen_time || '',
          'cu-type-name': 'CPS_Login',
        };
        Object.keys(fields).forEach((id) => {
          const el = document.getElementById(id);
          if (el) el.value = fields[id];
        });
      }
      if (resolve) resolve(true);
      await this.submitForm();
    },
    async submitForm() {
      try {
        const {
          sendFormEmail,
          getEmailProjectByReason,
          getEmailConfigFromDatabase,
        } = await import('../../../components/new-login/Common/email-sender.js');
        const {
          SUBMIT_STATUS_CODE,
          DEFAULT_SHOW_ID,
          DEFAULT_SHOW_CODE,
          getCurrentLangId,
        } = await import('../../../components/new-login/Common/new-login-config.js');

        const langId = this.form.langid || getCurrentLangId();
        const showId = this.form.showid || DEFAULT_SHOW_ID;
        const showCode = this.form.showcode || DEFAULT_SHOW_CODE;
        const reason = this.form.Reason;
        const dbEmailConfig = await getEmailConfigFromDatabase(
          showId,
          langId,
          getEmailProjectByReason(reason),
          showCode,
        );
        const userInfo = this.getUserInfo();
        this.fillUserInfo();

        const ticketEl = document.getElementById('cu-captcha-ticket');
        const randstrEl = document.getElementById('cu-captcha-randstr');
        const formDataToSend = {
          fid: this.form.fid || '',
          showid: showId,
          showcode: showCode,
          langid: langId,
          hidCodepage: this.form.hidCodepage || String(langId),
          submitPageURL: this.form.submitPageURL,
          IsNewValidCode: this.form.IsNewValidCode,
          isNotNeedValidate: 'true',
          captchaVerifyParam: typeof this.form.captchaVerifyParam === 'string'
            ? this.form.captchaVerifyParam
            : JSON.stringify(this.form.captchaVerifyParam || {}),
          Reason: reason,
          ReasonText: this.form.ReasonText || '',
          message: this.form.message || '',
          name: this.form.name || this.getUserField(userInfo, 'name', langId) || '',
          Company: this.form.Company || this.getUserField(userInfo, 'company', langId) || '',
          Country: this.form.Country || this.getUserField(userInfo, 'country', langId) || '',
          CountryText: this.form.CountryText || '',
          Mobile: this.form.Mobile || this.getUserField(userInfo, 'mobile', langId) || '',
          Email: this.form.Email || this.getUserField(userInfo, 'email', langId) || '',
          hdPrivacy: this.form.hdPrivacy ? 'true' : 'false',
          hdShare: this.form.hdShare ? 'true' : 'false',
          captcha_ticket: ticketEl ? ticketEl.value : '',
          captcha_randstr: randstrEl ? randstrEl.value : '',
        };

        const result = await sendFormEmail({
          formData: formDataToSend,
          captchaData: null,
          emailConfig: {
            email_to: this.form.Email || '',
            email_from: dbEmailConfig.FromEmail || 'emarketplace@adsale.com.hk',
            email_fromName: dbEmailConfig.FromName || '',
            email_replyTo: dbEmailConfig.ReplyTo || '',
            email_bcc: dbEmailConfig.BCC || '',
            email_cc: dbEmailConfig.CC || '',
            email_subject: dbEmailConfig.Title || 'eMarketplace - Contact Us',
            email_confirm_message: 'Send form Success',
          },
          formType: 'contact-us',
        });

        if (result?.statusCode === SUBMIT_STATUS_CODE.SUCCESS) {
          this.close();
          this.successModalVisible = true;
          this.form = { ...createForm(), submitPageURL: this.form.submitPageURL };
        } else if (result?.statusCode === SUBMIT_STATUS_CODE.CAPTCHA_FAILED) {
          this.$Alert(0, this.$t('lbl_EMPBE_Save_Failed_HasError', { msg: '验证码验证失败，请重试' }));
        } else {
          const errorMsg = result?.Msg
            ? (Array.isArray(result.Msg) ? result.Msg.join(', ') : result.Msg)
            : (result?.error || 'Unknown error');
          this.$Alert(0, this.$t('lbl_EMPBE_Save_Failed_HasError', { msg: errorMsg }));
        }
      } catch (error) {
        this.$Alert(0, this.$t('lbl_EMPBE_Save_Failed_HasError', { msg: error.message || 'Submit failed' }));
      } finally {
        this.submitting = false;
        this.loading = false;
      }
    },
    async fetchLoginInfo() {
      try {
        const res = await userService.GetLoginInfo();
        if (Array.isArray(res)) this.loginInfo = res;
      } catch (error) {
        // silent
      }
    },
    async fetchCurrentUserAccount() {
      try {
        const res = await userService.GetUserMyAccount();
        if (res?.data) this.currentUserAccount = res.data;
      } catch (error) {
        // silent
      }
    },
    async fetchCompanyInfo() {
      try {
        const user = this.$store?.state?.authentication?.user;
        if (!user?.empCompanyId) return;
        const res = await CompanyInfoService.GetCompanyInfoAsync({ companyId: user.empCompanyId });
        if (res?.isSuccessful && res.data) this.companyInfo = res.data;
      } catch (error) {
        // silent
      }
    },
    async detectIPLocation() {
      try {
        this.ipLocationInfo = await detectLocationByIP();
      } catch (error) {
        // silent
      }
    },
    async fillUserInfo() {
      const langId = parseInt(this.form.langid || currentLangId());
      const userInfo = this.getUserInfo();
      if (!userInfo || !Object.keys(userInfo).length) return;

      if (!this.form.name) {
        const name = this.getUserField(userInfo, 'name', langId);
        if (name) this.form.name = name;
      }
      if (!this.form.Email) {
        const email = this.getUserField(userInfo, 'email', langId);
        if (email) this.form.Email = email;
      }
      if (!this.form.Company) {
        const company = this.getUserField(userInfo, 'company', langId);
        if (company) this.form.Company = company;
      }
      if (!this.form.Mobile) {
        const mobile = this.getUserField(userInfo, 'mobile', langId);
        if (mobile) this.form.Mobile = mobile;
      }
      if (!this.form.Country && this.ipLocationInfo && this.countryOptions.length) {
        try {
          const matched = await autoDetectCountryCode(this.countryOptions);
          if (matched) {
            this.form.Country = matched;
            const opt = this.countryOptions.find((o) => o.value === matched);
            if (opt) this.form.CountryText = opt.text || '';
          }
        } catch (error) {
          // silent
        }
      }
    },
    getUserField(userInfo, field, langId) {
      if (!userInfo) return '';
      if (field === 'name') {
        if (langId === 1252 && userInfo.personNameEn) return userInfo.personNameEn;
        if (langId === 950 && userInfo.personNameTc) return userInfo.personNameTc;
        if (langId === 936 && userInfo.personNameSc) return userInfo.personNameSc;
        return userInfo.personNameEn || userInfo.personNameTc || userInfo.personNameSc || userInfo.name || '';
      }
      if (field === 'company') {
        if (langId === 1252 && userInfo.companyNameEn) return userInfo.companyNameEn;
        if (langId === 950 && userInfo.companyNameTc) return userInfo.companyNameTc;
        if (langId === 936 && userInfo.companyNameSc) return userInfo.companyNameSc;
        return userInfo.companyNameEn || userInfo.companyNameTc || userInfo.companyNameSc || userInfo.company || '';
      }
      if (field === 'email') return userInfo.emailValue || userInfo.email || '';
      if (field === 'mobile') return userInfo.phoneValue || userInfo.mobile || userInfo.phone || '';
      if (field === 'country') return userInfo.countryCode || userInfo.country || userInfo.countryId || '';
      return '';
    },
    getUserInfo() {
      let info = this.currentUserAccount ? { ...this.currentUserAccount } : {};
      const storeUser = this.$store?.state?.authentication?.user;
      if (storeUser) info = { ...info, ...storeUser };
      if (Array.isArray(this.loginInfo)) {
        this.loginInfo.forEach((item) => {
          if (item.type === 'email' && item.verifyLogin) {
            info.emailValue = item.verifyLogin;
            info.email = item.verifyLogin;
          } else if (item.type === 'mobile' && item.verifyLogin) {
            info.phoneValue = item.verifyLogin;
            info.mobile = item.verifyLogin;
          }
        });
      }
      if (this.companyInfo?.companyEmp) {
        const emp = this.companyInfo.companyEmp;
        info.companyNameEn = info.companyNameEn || emp.nameEng || emp.nameE;
        info.companyNameTc = info.companyNameTc || emp.nameTrad || emp.nameT;
        info.companyNameSc = info.companyNameSc || emp.nameSimp || emp.nameS;
      }
      return info;
    },
  },
};
</script>
